import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Dashboard,
  Insights,
  Speed,
  Warning,
  CompareArrows,
  Refresh,
  Psychology,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import InsightCard from '../insights/InsightCard';
import { fetchAllInsights } from '../../services/insightsApi';
import { Insight, InsightCategoryData, CATEGORY_COLORS } from '../../types/insights';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`insights-tabpanel-${index}`}
      aria-labelledby={`insights-tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const InsightsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [insights, setInsights] = useState<InsightCategoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadInsights = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllInsights();
      setInsights(data);
    } catch (err) {
      console.error('Failed to load insights:', err);
      setError('Failed to load insights. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleExploreWithAI = (prompt: string) => {
    navigate(`/?copilot=${encodeURIComponent(prompt)}`);
  };

  const categories = [
    { id: 'velocity', label: 'Velocity', icon: <Speed />, color: CATEGORY_COLORS.velocity },
    { id: 'risk', label: 'Risk Indicators', icon: <Warning />, color: CATEGORY_COLORS.risk },
    { id: 'correlation', label: 'Hidden Correlations', icon: <Insights />, color: CATEGORY_COLORS.correlation },
    { id: 'comparative', label: 'Comparative', icon: <CompareArrows />, color: CATEGORY_COLORS.comparative },
  ];

  const getInsightsForCategory = (categoryId: string): Insight[] => {
    if (!insights) return [];
    return (insights as any)[categoryId] || [];
  };

  const getTotalInsightsCount = (): number => {
    if (!insights) return 0;
    return (
      (insights.velocity?.length || 0) +
      (insights.risk?.length || 0) +
      (insights.correlation?.length || 0) +
      (insights.comparative?.length || 0)
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#1A202C' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#2D3748' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 700, color: 'white', mr: 1, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              FilmIQ
            </Typography>
            <Chip
              label="INSIGHTS"
              size="small"
              sx={{
                backgroundColor: '#9F7AEA',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
          </Box>

          {/* Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title="Dashboard">
              <IconButton
                onClick={() => navigate('/')}
                sx={{ color: 'white' }}
              >
                <Dashboard />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh Insights">
              <IconButton
                onClick={loadInsights}
                disabled={loading}
                sx={{ color: 'white' }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>

        {/* Title Section */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="h5" fontWeight={700} color="white">
                Production Insights
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Data-driven patterns that reveal why productions drift and what predicts trouble
              </Typography>
            </Box>
            {!loading && (
              <Chip
                icon={<Psychology sx={{ color: 'white !important' }} />}
                label={`${getTotalInsightsCount()} Insights Available`}
                sx={{
                  backgroundColor: '#4A5568',
                  color: 'white',
                  fontWeight: 500,
                }}
              />
            )}
          </Box>

          {/* Category Tabs */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: '1px solid #4A5568',
              '& .MuiTab-root': {
                color: '#A0AEC0',
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 48,
                '&.Mui-selected': {
                  color: 'white',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: categories[activeTab]?.color || '#4299E1',
              },
            }}
          >
            {categories.map((category, index) => (
              <Tab
                key={category.id}
                icon={category.icon}
                iconPosition="start"
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {category.label}
                    <Chip
                      label={getInsightsForCategory(category.id).length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: activeTab === index ? category.color : '#4A5568',
                        color: 'white',
                      }}
                    />
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Insights Content */}
        <Box sx={{ flexGrow: 1, minHeight: 350, overflow: 'auto', px: 3, pb: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            categories.map((category, index) => (
              <TabPanel key={category.id} value={activeTab} index={index}>
                <Grid container spacing={3}>
                  {getInsightsForCategory(category.id).length === 0 ? (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          textAlign: 'center',
                          py: 6,
                          color: 'text.secondary',
                        }}
                      >
                        <Insights sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" gutterBottom>
                          No {category.label} Insights Yet
                        </Typography>
                        <Typography variant="body2">
                          Insights will appear as more production data is collected and analyzed.
                        </Typography>
                      </Box>
                    </Grid>
                  ) : (
                    getInsightsForCategory(category.id).map((insight) => (
                      <Grid item xs={12} md={6} lg={4} key={insight.id}>
                        <InsightCard
                          insight={insight}
                          categoryColor={category.color}
                          onExploreWithAI={handleExploreWithAI}
                        />
                      </Grid>
                    ))
                  )}
                </Grid>
              </TabPanel>
            ))
          )}
        </Box>

      </Box>
    </Box>
  );
};

export default InsightsPage;
